import { type ComponentProps } from 'react';

import { classNames } from '@/lib/classNames';

export function Card({
    className,
    size = 'default',
    ...props
}: ComponentProps<'div'> & { size?: 'default' | 'sm' }) {
    return (
        <div
            data-slot="card"
            data-size={size}
            className={classNames(
                'group/card bg-card text-card-foreground flex flex-col overflow-hidden rounded-xl text-sm shadow-xs',
                'gap-(--card-spacing) py-(--card-spacing) [--card-spacing:--spacing(4)] data-[size=sm]:[--card-spacing:--spacing(4)] sm:[--card-spacing:--spacing(6)]',
                'ring-foreground/10 ring-1',
                'has-[>img:first-child]:pt-0 *:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl',
                className,
            )}
            {...props}
        />
    );
}

export function CardHeader({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-header"
            className={classNames(
                'group/card-header @container/card-header grid auto-rows-min items-start gap-1 rounded-t-xl px-(--card-spacing) has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-(--card-spacing)',
                className,
            )}
            {...props}
        />
    );
}

export function CardTitle({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-title"
            className={classNames(
                'font-heading text-base leading-normal font-medium select-none group-data-[size=sm]/card:text-sm',
                className,
            )}
            {...props}
        />
    );
}

export function CardDescription({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-description"
            className={classNames('text-muted-foreground text-sm select-none', className)}
            {...props}
        />
    );
}

export function CardAction({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-action"
            className={classNames(
                'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
                className,
            )}
            {...props}
        />
    );
}

export function CardContent({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-content"
            className={classNames('px-(--card-spacing)', className)}
            {...props}
        />
    );
}

export function CardFooter({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-footer"
            className={classNames(
                'flex items-center rounded-b-xl px-(--card-spacing) [.border-t]:pt-(--card-spacing)',
                className,
            )}
            {...props}
        />
    );
}
